import { useEffect, useState } from 'react';
import { ladeKickoffsFuerProjekte } from '../../services/kickoff/kickoffService';
import { describeError } from '../../services/errorMessage';
import { kickoffKennzeichen, kickoffsNachProjekt, type KickoffKennzeichen } from './kickoffStatusLogic';

/**
 * Wartezeit, bevor eine geänderte Projektmenge nachgeladen wird: die Menge
 * ändert sich bei jedem Tastendruck im Suchfeld (gleiche Bremse wie
 * useProjectLocks).
 */
const IDS_SETTLE_MS = 400;

const LEER: Map<string, KickoffKennzeichen> = new Map();

/** Noch nicht gespeicherte Projekte tragen die Platzhalter-Id 'temp-id'. */
const istEchteProjektId = (id: string | null | undefined): id is string => !!id && id !== 'temp-id';

/**
 * Kalender-Kennzeichen je Projekt für eine LISTE (Admin-Projektliste,
 * docs/projekt-kickoff-plan.md §4.4) — EIN Abruf für alle übergebenen Ids,
 * neu nur, wenn sich die Menge ändert, nicht bei jedem Rendern.
 *
 * FAIL-OPEN: Migration fehlt, Lesefehler oder keine Zeilen → leere Map, die
 * Liste sieht aus wie vor dem Kickoff.
 */
export const useKickoffKennzeichen = (projectIds: ReadonlyArray<string | null | undefined>): Map<string, KickoffKennzeichen> => {
    // Aus der je Render neuen Array-Instanz eine stabile Abhängigkeit machen.
    const idKey = Array.from(new Set(projectIds.filter(istEchteProjektId))).sort().join(',');
    const [kennzeichen, setKennzeichen] = useState<Map<string, KickoffKennzeichen>>(LEER);

    useEffect(() => {
        if (idKey === '') return;
        let abgebrochen = false;
        const timer = setTimeout(() => {
            ladeKickoffsFuerProjekte(idKey.split(','))
                .then(acks => {
                    if (abgebrochen) return;
                    const jetzt = new Date();
                    const map = new Map<string, KickoffKennzeichen>();
                    for (const [projectId, rows] of kickoffsNachProjekt(acks)) {
                        const k = kickoffKennzeichen(rows, jetzt);
                        if (k) map.set(projectId, k);
                    }
                    setKennzeichen(map);
                })
                .catch(e => {
                    console.warn('Kickoff-Kennzeichen nicht ladbar —', describeError(e));
                    if (!abgebrochen) setKennzeichen(LEER);
                });
        }, IDS_SETTLE_MS);
        return () => { abgebrochen = true; clearTimeout(timer); };
    }, [idKey]);

    // Bis die neue Seite geladen ist, bleiben die Kennzeichen der alten Map
    // stehen — sie sind nach Projekt-Id verschlüsselt, ein fremdes Projekt
    // bekommt also nie ein falsches Kennzeichen.
    return idKey === '' ? LEER : kennzeichen;
};
